/**
 * Remembers the invite-method tab (Direct invite / Company link / Public landing page)
 * last used for each session, so the invitees page reopens where the scheduler
 * left off. localStorage rather than a cookie: it's a pure UI preference that
 * has no business travelling to the API on every request.
 */

/**
 * Which of the three ways to reach attendees the scheduler is looking at. The
 * stored values stay "email" and "link" so a scheduler's remembered tab survives
 * the rename; "public" is new.
 */
export type InviteMethod = "email" | "link" | "public";

const keyFor = (sessionId: string) => `spaces.invite-method.${sessionId}`;

/** The tab last used for this session, or null when unknown (or storage is unavailable). */
export const getStoredInviteMethod = (
  sessionId: string,
): InviteMethod | null => {
  if (!sessionId) return null;
  try {
    const value = window.localStorage.getItem(keyFor(sessionId));
    return value === "email" || value === "link" ? value : null;
  } catch {
    // Storage can be unavailable (e.g. blocked in private browsing) — fall back to the default tab.
    return null;
  }
};

export const storeInviteMethod = (
  sessionId: string,
  method: InviteMethod,
): void => {
  if (!sessionId) return;
  try {
    window.localStorage.setItem(keyFor(sessionId), method);
  } catch {
    // Storage unavailable — the preference simply isn't remembered.
  }
};
